import React, { useState, useEffect } from "react";
import { FaArrowLeft } from "react-icons/fa";
import "../assets/css/ViewCustomer.css";
import axiosInstance from "../components/api/axiosInstance";

function ViewAccount({ accountId, onBack, onEditAccount }) {
  const [accountData, setAccountData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchAccount = async () => {
      setLoading(true);
      setError("");
      try {
        let account = null;
        try {
          const response = await axiosInstance.get(`/social-account/${accountId}`);
          if (response.data) {
            account = response.data.Account_Id ? response.data : (response.data.data || response.data);
          }
        } catch {
          const response = await axiosInstance.get("/accounts");
          const list = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.data)
              ? response.data.data
              : [];
          account = list.find(
            (item) => String(item.Account_Id || item.account_id) === String(accountId)
          ) || null;
        }
        setAccountData(account);
      } catch (error) {
        console.error("Error fetching account:", error);
        setError(error.response?.data?.message || "Failed to load account details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (accountId) {
      fetchAccount();
    }
  }, [accountId]);

  const InfoItem = ({ label, value }) => {
    const hasValue = value && value !== "—" && value !== "";
    return (
      <div className="viewlead-info-item">
        <span className={`viewlead-info-label ${hasValue ? 'has-value' : ''}`}>{label}: </span>
        <span className={`viewlead-info-value ${!hasValue ? 'muted' : ''}`}>
          {hasValue ? value : "—"}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div className="viewlead-loading">Loading account details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="viewlead-container">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back to Accounts
        </button>
        <div className="error-message" style={{ 
          backgroundColor: '#fee', 
          color: '#c33', 
          padding: '12px', 
          borderRadius: '4px', 
          marginBottom: '20px',
          fontSize: '14px'
        }}>{error}</div>
      </div>
    );
  }

  if (!accountData) {
    return (
      <div className="viewlead-container">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back to Accounts
        </button>
        <div className="viewlead-loading">Account not found.</div>
      </div>
    );
  }

  const account = accountData;

  return (
    <div className="viewlead-container">
      {/* Header */}
      <div className="viewlead-header">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back to Accounts
        </button>
        <button className="btn-edit-lead" onClick={() => onEditAccount && onEditAccount(accountId)}>
          Edit Account
        </button>
      </div>

      {/* Account Info Card */}
      <div className="viewlead-card">
        {/* Account Information */}
        <h3 className="viewlead-section-title">Account Information</h3>
        <div
          className="viewlead-info-grid"
          style={{ gridTemplateColumns: "repeat(3, 1fr)" }}
        >
          <InfoItem 
            label="Account ID" 
            value={String(account.Account_Id || account.account_id || "")} 
          />
          <InfoItem label="Platform Name" value={account.Platform_Name || account.platform_name} />
          <InfoItem label="Account Name" value={account.Account_Name || account.account_name} />
          <InfoItem label="Username" value={account.Username || account.username} />
          <InfoItem label="Status" value={account.Status || account.status} />
        </div>

        {/* Contact Information */}
        <h3 className="viewlead-section-title">Contact Information</h3>
        <div className="viewlead-info-grid">
          <div
            style={{
              display: "flex",
              gap: "20px",
              flexWrap: "wrap",
              gridColumn: "1 / -1",
              alignItems: "stretch",
            }}
          >
            <div style={{ flex: "1 1 240px" }}>
              <InfoItem
                label="Contact Number"
                value={account.Contact_Number || account.contact_number}
              />
            </div>
            <div style={{ flex: "1 1 240px" }}>
              <InfoItem
                label="Email Used"
                value={account.Email_Used || account.email_used}
              />
            </div>
          </div>
        </div>

        {/* URL Information */}
        <h3 className="viewlead-section-title">URL Information</h3>
        <div className="viewlead-info-grid">
          <div className="viewlead-info-item">
            <span className={`viewlead-info-label ${(account.Profile_URL || account.profile_url || account.URL || account.url) ? 'has-value' : ''}`}>
              Profile URL: 
            </span>
            <span className={`viewlead-info-value ${!(account.Profile_URL || account.profile_url || account.URL || account.url) ? 'muted' : ''}`}>
              {account.Profile_URL || account.profile_url || account.URL || account.url ? (
                <a 
                  href={account.Profile_URL || account.profile_url || account.URL || account.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ color: "#2977ed", textDecoration: "none" }}
                >
                  {account.Profile_URL || account.profile_url || account.URL || account.url}
                </a>
              ) : "—"}
            </span>
          </div>
          <div className="viewlead-info-item">
            <span className={`viewlead-info-label ${(account.Business_URL || account.business_url || account.URL || account.url) ? 'has-value' : ''}`}>
              Business URL: 
            </span>
            <span className={`viewlead-info-value ${!(account.Business_URL || account.business_url || account.URL || account.url) ? 'muted' : ''}`}>
              {account.Business_URL || account.business_url || account.URL || account.url ? (
                <a
                  href={account.Business_URL || account.business_url || account.URL || account.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#2977ed", textDecoration: "none" }}
                >
                  {account.Business_URL || account.business_url || account.URL || account.url}
                </a>
              ) : "—"}
            </span>
          </div>
        </div>

        {/* Additional Information */}
        <h3 className="viewlead-section-title">Additional Information</h3>
        <div className="viewlead-info-grid">
          <InfoItem 
            label="Notes" 
            value={account.Notes || account.notes || "—"} 
          />
        </div>
      </div>
    </div>
  );
}

export default ViewAccount;
